import {XIcon} from '@heroicons/react/outline';
import type {JSX} from 'react';
import {useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';

import {Button, DatePicker, Input, Select} from '@/components';
import {Filter} from '@/components/Filter';
import {
  getBusinessTransactionChannelsOptions,
  getBusinessTransactionStatusOptions,
  getBusinessTransactionTypesOptions,
  getTransactionProcessorsOptions,
} from '@/constants/formatters';
import {useCountryList} from '@/context/CountryListContext';
import type {AfriexTransactionStatus} from '@/types';
import {mapCountryListToCurrencyOptions} from '@/utils/countries';

import DownloadBusinessTransactionCSVButton from './DownloadBusinessTransactionCSVButton';
import type {TransactionFiltersProps} from './types';

export default function TransactionFilters({
  transactions,
  exportFilters,
  filterCount,
  currentFilters,
  filtersToApply,
  clearFilters,
  selectAppliedFilters,
  handleApplyFilters,
  selectCurrentFilters,
  disableAdvancedFilters,
  currentTabId,
}: TransactionFiltersProps & {
  currentTabId?: 'swift' | 'system' | 'all';
}): JSX.Element {
  const {userId, businessId} = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get('status') as AfriexTransactionStatus;
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const currencies = mapCountryListToCurrencyOptions(countryList);

  const fromDate = filtersToApply.fromDate
    ? new Date(filtersToApply.fromDate)
    : undefined;

  const toDate = filtersToApply.toDate
    ? new Date(filtersToApply.toDate)
    : undefined;

  const handleShowFilterChange = (): void => {
    setShowFilters(!showFilters);
  };

  const handleApplyFilter = (): void => {
    // Only close on success. Dismissing the panel after a rejected date range
    // hid the values the user has to correct. See hooks/useFilters.ts.
    if (handleApplyFilters()) {
      setShowFilters(false);
    }
  };

  const handleClearFilters = (): void => {
    clearFilters();
  };
  return (
    <div className="mt-5 w-full">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* Filter Box */}
          <Filter
            closeFilter={() => setShowFilters(false)}
            handleShowFilter={handleShowFilterChange}
            label="Filters"
            data-cy="merchant-transactions-filter-toggle"
            positionLeft={1}
            bodyClassName="w-full md:w-[600px]"
            disabled={disableAdvancedFilters}
            title={
              disableAdvancedFilters ? 'Filters are disabled for this view' : ''
            }
          >
            {/* Filter Body */}
            {showFilters && (
              <div
                className="p-4 space-y-4 max-w-4xl mx-auto w-full col-span-full"
                data-cy="merchant-transactions-filter-panel"
              >
                <div className="flex flex-col gap-4">
                  {/* First Row */}
                  <div className="flex flex-col md:flex-row items-center gap-2">
                    <Input
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        selectAppliedFilters('transactionId', e.target.value)
                      }
                      name="transactionId"
                      label="Transaction ID or reference"
                      type="text"
                      value={filtersToApply.transactionId ?? ''}
                      className="w-full isFull"
                      placeholder="ID, merchant, internal or processor reference"
                      data-cy="merchant-transactions-filter-transaction-id"
                    />
                    {!userId && (
                      <Input
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          selectAppliedFilters('userId', e.target.value)
                        }
                        name="userId"
                        label="User ID"
                        type="text"
                        value={filtersToApply.userId ?? ''}
                        className="w-full isFull"
                        placeholder="Enter User ID"
                        data-cy="merchant-transactions-filter-user-id"
                      />
                    )}
                    {!businessId && (
                      <Input
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          selectAppliedFilters('businessId', e.target.value)
                        }
                        name="businessId"
                        label="Business ID"
                        type="text"
                        value={filtersToApply.businessId ?? ''}
                        className="w-full isFull"
                        placeholder="Enter Business ID"
                        data-cy="merchant-transactions-filter-business-id"
                      />
                    )}
                  </div>

                  {/* Second Row */}
                  <div className="flex flex-col md:flex-row gap-2">
                    {/* react-datepicker only forwards a whitelist of props to its
                        input, so data-cy goes on a wrapper and the page object
                        reaches the input through it. */}
                    <div data-cy="merchant-transactions-filter-from-date">
                      <DatePicker
                        label="From Date"
                        selectedDate={fromDate}
                        setSelectedDate={(date) =>
                          selectAppliedFilters('fromDate', date)
                        }
                        // Constrain each picker by the other, so an invalid
                        // range cannot be selected at all. Apply still rejects
                        // one that was typed rather than picked.
                        maxDate={toDate}
                        placeholderText="Select Date"
                      />
                    </div>
                    <div data-cy="merchant-transactions-filter-to-date">
                      <DatePicker
                        label="To Date"
                        selectedDate={toDate}
                        setSelectedDate={(date) =>
                          selectAppliedFilters('toDate', date)
                        }
                        minDate={fromDate}
                        placeholderText="Select Date"
                      />
                    </div>
                    <Select
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        selectAppliedFilters('channel', e.target.value)
                      }
                      value={filtersToApply.channel}
                      name="channel"
                      options={getBusinessTransactionChannelsOptions()}
                      placeholder="Select"
                      label="Payment Method"
                      data-cy="merchant-transactions-filter-channel"
                    />
                  </div>

                  {/* Third Row */}
                  <div className="flex flex-col md:flex-row gap-2">
                    <Select
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        selectAppliedFilters('type', e.target.value)
                      }
                      value={filtersToApply.type}
                      name="type"
                      options={getBusinessTransactionTypesOptions()}
                      placeholder="Select"
                      label="Filter by Type"
                      data-cy="merchant-transactions-filter-type"
                    />
                    <Select
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        selectAppliedFilters('processor', e.target.value)
                      }
                      value={filtersToApply.processor}
                      name="processor"
                      options={getTransactionProcessorsOptions()}
                      placeholder="Select"
                      label="Filter by Processor"
                      data-cy="merchant-transactions-filter-processor"
                    />
                    <Select
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        selectAppliedFilters('currency', e.target.value)
                      }
                      value={filtersToApply.currency}
                      name="currency"
                      options={currencies}
                      placeholder="Select"
                      label="Filter by Currency"
                      data-cy="merchant-transactions-filter-currency"
                    />
                  </div>
                </div>

                <div className="flex md:justify-end">
                  <Button
                    onClick={handleClearFilters}
                    colorScheme="cyan"
                    variant="ghost"
                    data-cy="merchant-transactions-filter-clear"
                  >
                    Clear
                  </Button>

                  <Button
                    onClick={handleApplyFilter}
                    colorScheme="cyan"
                    disabled={filterCount === 0}
                    className="ml-3"
                    data-cy="merchant-transactions-filter-apply"
                  >
                    Apply Filters {filterCount && ` (${filterCount})`}
                  </Button>
                </div>
              </div>
            )}
          </Filter>
        </div>

        {/* Status Select Box */}
        <div className="flex flex-wrap gap-3 items-center md:space-x-3">
          {(filterCount > 0 || currentFilters.status) &&
            (currentTabId === 'swift' || currentTabId === 'system') && (
              <Button
                onClick={handleClearFilters}
                colorScheme="cyan"
                variant="outline"
                className="p-2 flex items-center justify-center border-gray-300 dark:border-gray-700"
                title="Clear all filters"
              >
                <XIcon className="h-5 w-5" />
              </Button>
            )}
          {!status && (
            <Select
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                selectCurrentFilters('status', e.target.value)
              }
              value={currentFilters.status}
              name="status"
              options={getBusinessTransactionStatusOptions()}
              placeholder="Filter by Status"
              data-cy="merchant-transactions-filter-status"
            />
          )}

          <DownloadBusinessTransactionCSVButton
            filters={exportFilters}
            disabled={!transactions.length}
            currentTabId={currentTabId}
          />
        </div>
      </div>
    </div>
  );
}
